import { addMilliseconds, differenceInMilliseconds, formatISO, parseISO } from 'date-fns'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { Caption1 } from '@ui/Typography'

import { formatDayMonthShort } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { calculatePercentage, clampPercent } from '@utils/numbers.utils'

import { MultiLevelCommissionDetailDto } from '@model/MultiLevelCommission'

import { useFormatNumber } from '@hooks/useFormatNumber'

import {
  ProgressBarsBlock,
  ProgressBarsBlockItem,
  ProgressBarsBlockSection,
} from '@shared/components/ProgressBarsBlock/ProgressBarsBlock'

import styles from './PayoutScheme.module.scss'

interface ProgressBarsProps {
  multiLevelKpiActivity?: MultiLevelCommissionDetailDto
}

interface TimeRange {
  start: string
  end: string
}

export const ProgressBars: FC<ProgressBarsProps> = (props) => {
  const { multiLevelKpiActivity } = props
  const isMobile = useWindowResize()
  const locale = useSessionLanguage()
  const { formatNumber } = useFormatNumber()

  const { t } = useTranslation()

  const getClientLabel = useCallback(
    (amount?: number): string => {
      if (!amount) {
        return ''
      }

      return amount === 1
        ? `${amount} ${t('IB.Dashboard.Client')}`
        : `${amount} ${t('IB.Dashboard.Clients')}`
    },
    [t]
  )

  const equalTimeRanges = useMemo<TimeRange[]>(() => {
    if (!multiLevelKpiActivity) {
      return []
    }

    const startDate = parseISO(multiLevelKpiActivity.periodStartDate)
    const endDate = parseISO(multiLevelKpiActivity.periodEndDate)

    const totalDuration = differenceInMilliseconds(endDate, startDate)

    const segmentDuration = totalDuration / 3

    return [
      {
        start: formatISO(startDate),
        end: formatISO(addMilliseconds(startDate, segmentDuration)),
      },
      {
        start: formatISO(addMilliseconds(startDate, segmentDuration)),
        end: formatISO(addMilliseconds(startDate, 2 * segmentDuration)),
      },
      {
        start: formatISO(addMilliseconds(startDate, 2 * segmentDuration)),
        end: formatISO(endDate),
      },
    ]
  }, [multiLevelKpiActivity])

  const items = useMemo<ProgressBarsBlockItem[]>(() => {
    const clientsCurrent = multiLevelKpiActivity?.clients.current
    const clientsRequired = multiLevelKpiActivity?.clients.required

    const clientsPercent = calculatePercentage(clientsCurrent, clientsRequired)

    const volumeInMillionUsdCurrent = multiLevelKpiActivity?.volumeInMillionUsd.current
    const volumeInMillionUsdRequired = multiLevelKpiActivity?.volumeInMillionUsd.required

    const volumeInMillionUsdPercent = calculatePercentage(
      volumeInMillionUsdCurrent,
      volumeInMillionUsdRequired
    )

    const newClientsSections: ProgressBarsBlockSection[] = [
      {
        ...(!isMobile
          ? {
              label: `${clientsCurrent} / ${clientsRequired}`,
            }
          : {}),
        percent: clientsPercent,
        isCurrent: true,
        stripesVariant: 'info',
      },
    ]

    const volumeSections: ProgressBarsBlockSection[] = [
      {
        ...(!isMobile
          ? {
              label: `${
                formatNumber(volumeInMillionUsdCurrent, {
                  minDecimalPlaces: 0,
                }) ?? '-'
              } / ${volumeInMillionUsdRequired ?? '-'} M`,
            }
          : {}),
        percent: volumeInMillionUsdPercent,
        isCurrent: true,
        stripesVariant: 'info',
      },
    ]

    const timeLeftSections: ProgressBarsBlockSection[] =
      equalTimeRanges.map<ProgressBarsBlockSection>((timeRange, index) => {
        const totalPeriod = differenceInMilliseconds(
          parseISO(timeRange.end),
          parseISO(timeRange.start)
        )
        const elapsed = differenceInMilliseconds(new Date(), parseISO(timeRange.start))
        const percent = clampPercent((elapsed / totalPeriod) * 100)

        const isFirst = index === 0
        const isLast = index === equalTimeRanges.length - 1

        return {
          label: isFirst
            ? formatDayMonthShort(timeRange.start, locale)
            : isLast
            ? formatDayMonthShort(timeRange.end, locale)
            : undefined,
          labelPosition: isFirst ? 'flex-start' : isLast ? 'flex-end' : undefined,
          percent,
          isCurrent: true,
          stripesVariant: 'info',
        }
      })

    return [
      {
        title: <Caption1 color='text-high-em'>{t('IB.Dashboard.Metrics.New clients')}</Caption1>,
        value: isMobile ? (
          <Caption1 color='text-med-em'>{`${clientsCurrent} / ${clientsRequired}`}</Caption1>
        ) : null,
        tooltip: t('IB.Dashboard.New clients registered'),
        progressTooltip: clientsCurrent
          ? getClientLabel(clientsCurrent)
          : '0 ' + t('IB.Dashboard.Clients'),
        sections: newClientsSections,
      },

      {
        title: <Caption1 color='text-high-em'>{t('IB.Dashboard.Volume')}</Caption1>,
        value: isMobile ? (
          <Caption1 color='text-med-em'>{`${
            multiLevelKpiActivity?.volumeInMillionUsd.current ?? '-'
          } / ${multiLevelKpiActivity?.volumeInMillionUsd.required ?? '-'}`}</Caption1>
        ) : null,
        tooltip: t('IB.Dashboard.Volume generated'),
        progressTooltip: multiLevelKpiActivity?.volumeInMillionUsd.current
          ? `${multiLevelKpiActivity.volumeInMillionUsd.current} M`
          : '0 M',
        sections: volumeSections,
      },

      {
        title: <Caption1 color='text-high-em'>{t('IB.Dashboard.Time left')}</Caption1>,
        value: isMobile ? '' : null,
        tooltip: t('IB.Dashboard.Qualification window'),
        sections: timeLeftSections,
      },
    ]
  }, [
    multiLevelKpiActivity?.clients,
    multiLevelKpiActivity?.volumeInMillionUsd,
    isMobile,
    formatNumber,
    equalTimeRanges,
    t,
    getClientLabel,
    locale,
  ])

  return (
    <ProgressBarsBlock
      items={items}
      color='purple'
      className={styles.progressBarsBlock}
      size='small'
      useBodyTypography={false}
    />
  )
}
